// Admin dashboard state: portfolio metrics, community filtering, and the
// association onboarding wizard with its document-ingestion flow.
//
// Reads live data from the backend when the admin endpoints are available and
// falls back to SEED_COMMUNITIES so the page is demoable offline.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "./api.js";
import { useDirectory } from "./admin/useDirectory";

export const SEED_COMMUNITIES = [
  { id: "oakridge", name: "Oakridge Homeowners Association", address: "1420 Oakridge Way", city: "Milpitas", state: "CA", zip: "95035", units: 148, docs: 3, resolutions: 6, sections: 412, status: "Active", plan: "Board + Resident", manager: "Marcus Okonkwo", indexed: "Mar 2026", requests: 12 },
  { id: "hillcrest", name: "Hillcrest Terrace Owners", address: "88 Hillcrest Terrace", city: "San Jose", state: "CA", zip: "95124", units: 92, docs: 4, resolutions: 3, sections: 288, status: "Active", plan: "Board only", manager: "Dana Whitfield", indexed: "Feb 2026", requests: 5 },
  { id: "marina", name: "Marina Bluff Condominiums", address: "7 Marina Bluff Drive", city: "Alameda", state: "CA", zip: "94501", units: 210, docs: 5, resolutions: 9, sections: 631, status: "Active", plan: "Board + Resident", manager: "Priya Raman", indexed: "Mar 2026", requests: 21 },
  { id: "sunridge", name: "Sunridge Village HOA", address: "3300 Sunridge Village Loop", city: "Fremont", state: "CA", zip: "94538", units: 64, docs: 2, resolutions: 2, sections: 176, status: "Onboarding", plan: "Trial", manager: "Unassigned", indexed: "—", requests: 0 },
  { id: "cedarpark", name: "Cedar Park Estates", address: "215 Cedar Park Avenue", city: "Pleasanton", state: "CA", zip: "94566", units: 118, docs: 3, resolutions: 4, sections: 344, status: "Active", plan: "Board only", manager: "Luis Arroyo", indexed: "Jan 2026", requests: 8 },
  { id: "lakeview", name: "Lakeview Commons Association", address: "940 Lakeview Commons", city: "Union City", state: "CA", zip: "94587", units: 76, docs: 2, resolutions: 1, sections: 198, status: "Suspended", plan: "Board only", manager: "Unassigned", indexed: "Nov 2025", requests: 0 },
];

export const STATUS_FILTERS = ["All", "Active", "Onboarding", "Suspended"];
// Units/Residents (steps 04/05, added 2026-09-21) come from
// src/admin/DirectorySteps.tsx -- see HANDOFF-ADMIN.md and the integration
// plan §2.3/§4 step 2 for why they were unwired until now.
export const WIZARD_STEPS = ["Community", "CC&Rs", "Documents", "Units", "Residents"];
const LAST_STEP = WIZARD_STEPS.length - 1;
const FILE_STATUS = { uploading: "Uploading", indexing: "Indexing", indexed: "Indexed" };

export const slugify = (name) =>
  String(name || "community").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const num = (n) => String(n ?? 0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/** "1420 Oakridge Way · Milpitas, CA · 95035" */
export const placeOf = (c) =>
  [c.address, [c.city, c.state].filter(Boolean).join(", "), c.zip].filter(Boolean).join(" · ") || "—";

const now = () => {
  const d = new Date();
  return [d.getHours(), d.getMinutes(), d.getSeconds()].map((n) => String(n).padStart(2, "0")).join(":");
};

const EMPTY_WIZARD = {
  step: 0,
  name: "",
  street: "",
  city: "",
  state: "",
  zip: "",
  units: "",
  addressFile: null,
  addressRows: 0,
  files: [],
};

export function useAdmin() {
  const [communities, setCommunities] = useState(SEED_COMMUNITIES);
  const [filter, setFilter] = useState("");
  const [status, setStatus] = useState("All");
  const [selectedId, setSelectedId] = useState(null);
  const [toast, setToast] = useState("");

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizard, setWizard] = useState(EMPTY_WIZARD);
  const [log, setLog] = useState([]);
  const [logOpen, setLogOpen] = useState(true);

  // Set once step 00 is confirmed (see ensureCommunity below) so the
  // Units/Residents steps have a real community to read/write against
  // instead of only local wizard state.
  const [communityId, setCommunityId] = useState(null);

  const timers = useRef([]);
  const toastTimer = useRef(null);
  useEffect(() => () => {
    // ids come from both setTimeout and setInterval; clear as both.
    timers.current.forEach((id) => { clearTimeout(id); clearInterval(id); });
    clearTimeout(toastTimer.current);
  }, []);

  const flash = useCallback((text, ms = 3600) => {
    clearTimeout(toastTimer.current);
    setToast(text);
    toastTimer.current = setTimeout(() => setToast(""), ms);
  }, []);

  const pushLog = useCallback((line, kind = "info") => {
    setLog((prev) => [...prev, { id: "l" + Date.now() + Math.random(), at: now(), line, kind }]);
  }, []);

  // Live portfolio, when the backend exposes it.
  useEffect(() => {
    let alive = true;
    api
      .listAdminCommunities()
      .then((rows) => {
        if (alive && Array.isArray(rows) && rows.length) setCommunities(rows);
      })
      .catch(() => { /* seeded data stands in */ });
    return () => { alive = false; };
  }, []);

  const patchWizard = useCallback((patch) => setWizard((w) => ({ ...w, ...patch })), []);

  const openWizard = useCallback(() => {
    setWizard(EMPTY_WIZARD);
    setCommunityId(null);
    setLog([]);
    setLogOpen(true);
    setWizardOpen(true);
  }, []);

  const closeWizard = useCallback(() => setWizardOpen(false), []);

  // Creates the community record as soon as step 00 is confirmed, rather
  // than waiting for "Finish setup" -- the Units/Residents steps (04/05)
  // need a real community_id to post to. Idempotent: returns the existing
  // id on every call after the first. Falls back to a local-only id (no
  // server record) if the create request fails, same demo-mode posture as
  // uploadDocument/uploadAddresses below.
  const ensureCommunity = useCallback(async () => {
    if (communityId) return communityId;
    const id = slugify(wizard.name) || "community-" + Date.now();
    const record = {
      id,
      name: wizard.name.trim() || "New community",
      address: wizard.street.trim(),
      city: wizard.city.trim(),
      state: wizard.state.trim().toUpperCase(),
      zip: wizard.zip.trim(),
      units: parseInt(wizard.units, 10) || wizard.addressRows || 0,
      status: "Onboarding",
      plan: "Trial",
    };
    pushLog(`POST /api/v1/communities/${id} · status=onboarding`, "req");
    try {
      await api.createAdminCommunity(record);
      pushLog(`200 created · onboarding started`, "ok");
    } catch (err) {
      pushLog(`${err.status || "ERR"} ${err.message} — continuing in local/demo mode`, "err");
    }
    setCommunityId(id);
    return id;
  }, [communityId, wizard, pushLog]);

  const dir = useDirectory(communityId, [], [], pushLog);

  /** Upload → chunk → embed → indexed. Real POST when a File is supplied. */
  const uploadDocument = useCallback(
    async (label, file, { name, sections } = {}) => {
      const fileName = name || file?.name || slugify(label) + ".pdf";
      const id = "f" + Date.now() + Math.round(Math.random() * 999);
      const slug = slugify(wizard.name);
      const sectionCount = sections ?? 0;

      setWizard((w) =>
        w.files.some((f) => f.label === label)
          ? w
          : { ...w, files: [...w.files, { id, name: fileName, label, sections: sectionCount, status: "uploading", pct: 6 }] }
      );
      pushLog(`POST /api/v1/communities/${slug}/documents — ${fileName}`, "req");

      const patchFile = (fn) =>
        setWizard((w) => ({ ...w, files: w.files.map((f) => (f.id === id ? fn(f) : f)) }));

      if (file) {
        try {
          const res = label === "CC&Rs" ? await api.uploadCcrs(file) : await api.uploadDocument(file, label);
          patchFile((f) => ({ ...f, pct: 100, status: "indexed", sections: res?.sections ?? f.sections }));
          pushLog(`200 indexed · ${fileName} searchable`, "ok");
        } catch (err) {
          patchFile((f) => ({ ...f, status: "uploading", pct: 0, error: err.message }));
          pushLog(`${err.status || "ERR"} ${err.message}`, "err");
        }
        return;
      }

      // Demo path: animate the same lifecycle the API reports.
      let pct = 6;
      const tick = setInterval(() => {
        pct = Math.min(100, pct + 17);
        patchFile((f) => ({ ...f, pct }));
        if (pct < 100) return;
        clearInterval(tick);
        patchFile((f) => ({ ...f, status: "indexing" }));
        pushLog(`202 accepted · job_${id.slice(1, 8)} · chunking ${sectionCount} sections`, "ok");
        const done = setTimeout(() => {
          pushLog(`POST /embeddings · ${sectionCount}/${sectionCount} vectors upserted`, "req");
          patchFile((f) => ({ ...f, status: "indexed" }));
          pushLog(`200 indexed · ${fileName} searchable`, "ok");
        }, 1000);
        timers.current.push(done);
      }, 240);
      timers.current.push(tick);
    },
    [wizard.name, pushLog]
  );

  const uploadAddresses = useCallback(
    async (file) => {
      const slug = slugify(wizard.name);
      if (file) {
        try {
          const res = await api.uploadAddresses(slug, file);
          patchWizard({ addressFile: file.name, addressRows: res?.rows ?? 0 });
          pushLog(`POST /api/v1/communities/${slug}/addresses · ${res?.rows ?? 0} rows parsed`, "req");
        } catch (err) {
          pushLog(`${err.status || "ERR"} ${err.message}`, "err");
        }
        return;
      }
      patchWizard({ addressFile: "addresses-148.csv", addressRows: 148 });
      pushLog(`POST /api/v1/communities/${slug}/addresses · 148 rows parsed`, "req");
    },
    [wizard.name, patchWizard, pushLog]
  );

  const finishWizard = useCallback(async () => {
    const id = await ensureCommunity();
    const indexed = wizard.files.filter((f) => f.status === "indexed");
    const sections = indexed.reduce((a, f) => a + (f.sections || 0), 0);
    const record = {
      id,
      name: wizard.name.trim() || "New community",
      address: wizard.street.trim(),
      city: wizard.city.trim(),
      state: wizard.state.trim().toUpperCase(),
      zip: wizard.zip.trim(),
      units: parseInt(wizard.units, 10) || wizard.addressRows || dir.units.length || 0,
      docs: indexed.length,
      resolutions: 0,
      sections,
      status: "Active",
      plan: "Trial",
      manager: "Unassigned",
      indexed: new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" }),
      requests: 0,
    };

    // The record was already created by ensureCommunity() back at step 00
    // -- this is a status/metadata PATCH now that onboarding is complete
    // (docs indexed, units/residents on file), not a fresh create.
    pushLog(`PATCH /api/v1/communities/${id} · status=active`, "req");
    try {
      await api.patchAdminCommunity(id, {
        name: record.name, address: record.address, city: record.city, state: record.state,
        zip: record.zip, units: record.units, docs: record.docs, sections: record.sections,
        status: record.status, plan: record.plan,
      });
      pushLog(`200 updated · ${record.docs} documents · ${sections} sections · ${dir.units.length} units · ${dir.residents.length} residents`, "ok");
    } catch {
      pushLog(`200 updated (local) · ${record.docs} documents · ${sections} sections live`, "ok");
    }

    setCommunities((prev) => [record, ...prev.filter((c) => c.id !== record.id)]);
    setSelectedId(record.id);
    setFilter("");
    setStatus("All");
    setWizardOpen(false);
    flash(`${record.name} onboarded · ${dir.units.length} units · ${dir.residents.length} residents`);
  }, [wizard, pushLog, flash, ensureCommunity, dir.units, dir.residents]);

  const list = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const scope = status === "All" ? communities : communities.filter((c) => c.status === status);
    if (!q) return scope;
    return scope.filter((c) =>
      [c.name, c.address, c.city, c.state, c.zip].some((v) => String(v || "").toLowerCase().includes(q))
    );
  }, [communities, filter, status]);

  const selected = useMemo(() => communities.find((c) => c.id === selectedId) || null, [communities, selectedId]);

  const metricsTop = useMemo(
    () => [
      { key: "Communities", value: num(communities.length), note: `${communities.filter((c) => c.status === "Active").length} active` },
      { key: "Documents", value: num(communities.reduce((a, c) => a + (c.docs || 0), 0)), note: "Across all portfolios" },
      { key: "Resolutions", value: num(communities.reduce((a, c) => a + (c.resolutions || 0), 0)), note: "Adopted and on file" },
    ],
    [communities]
  );

  const metricsBottom = useMemo(
    () => [
      { key: "Metric four", value: "—", note: "Placeholder" },
      { key: "Metric five", value: "—", note: "Placeholder" },
      { key: "Metric six", value: "—", note: "Placeholder" },
    ],
    []
  );

  const detailCards = useMemo(() => {
    if (!selected) return [];
    return [
      { key: "Units", value: num(selected.units), note: "Billable doors" },
      { key: "Documents", value: num(selected.docs), note: `${num(selected.sections)} sections indexed` },
      { key: "Resolutions", value: num(selected.resolutions), note: "Adopted and on file" },
      { key: "Open requests", value: num(selected.requests), note: "Resident queue" },
      { key: "Community manager", value: selected.manager || "Unassigned", note: "Primary contact" },
      { key: "Plan", value: selected.plan, note: `Last indexed ${selected.indexed}` },
      {
        key: "Mailing address",
        value: selected.address || "—",
        note: [[selected.city, selected.state].filter(Boolean).join(", "), selected.zip].filter(Boolean).join(" ") || "Not on file",
      },
    ];
  }, [selected]);

  const ccrs = wizard.files.filter((f) => f.label === "CC&Rs");
  const ccrIndexed = ccrs.some((f) => f.status === "indexed");
  const indexedFiles = wizard.files.filter((f) => f.status === "indexed");

  return {
    // dashboard
    communities, list, metricsTop, metricsBottom, detailCards,
    filter, setFilter, status, setStatus, STATUS_FILTERS,
    selected, selectedId,
    toggleSelect: useCallback((id) => setSelectedId((cur) => (cur === id ? null : id)), []),
    // Force-set (not toggle) -- for the #/admin/communities/{id} route in
    // AdminApp.jsx, which needs to select whatever the URL says regardless
    // of what was selected before, and re-select the same id on a hash
    // re-fire without deselecting it the way toggleSelect would.
    selectId: useCallback((id) => setSelectedId(id), []),
    clearSelection: useCallback(() => setSelectedId(null), []),
    toast,

    // wizard
    wizardOpen, openWizard, closeWizard,
    wizard, patchWizard,
    communityId,
    dir,
    setStep: useCallback((step) => patchWizard({ step }), [patchWizard]),
    back: useCallback(() => patchWizard({ step: Math.max(0, wizard.step - 1) }), [patchWizard, wizard.step]),
    next: useCallback(async () => {
      // Step 00 -> 01 is also where the community record actually gets
      // created (see ensureCommunity) so Units/Residents (03/04) have a
      // real community_id to read and write against.
      if (wizard.step === 0) await ensureCommunity();
      patchWizard({ step: Math.min(LAST_STEP, wizard.step + 1) });
    }, [patchWizard, wizard.step, ensureCommunity]),
    canAdvance:
      wizard.step === 0 ? wizard.name.trim().length > 1
      : wizard.step === 1 || wizard.step === 2 ? ccrIndexed
      : wizard.step === 3 ? dir.units.length > 0
      : true, // step 4 (Residents) -- optional; Finish setup is always available here
    nextLabel: wizard.step === 0 ? "Save & continue" : "Continue",
    ccrFiles: ccrs.map((f) => ({ ...f, statusLabel: FILE_STATUS[f.status] })),
    otherFiles: wizard.files.filter((f) => f.label !== "CC&Rs").map((f) => ({ ...f, statusLabel: FILE_STATUS[f.status] })),
    corpusSummary: indexedFiles.length
      ? `${indexedFiles.length} documents · ${num(indexedFiles.reduce((a, f) => a + (f.sections || 0), 0))} sections embedded and searchable`
      : "No documents indexed yet",
    uploadDocument, uploadAddresses, finishWizard,
    log, logOpen, toggleLog: useCallback(() => setLogOpen((v) => !v), []),
    endpoint: `POST /api/v1/communities/${slugify(wizard.name)}/documents`,
  };
}
